import { useHasPermission } from "../auth/useClaims";
import { useGetSelfPayCeilingQuery } from "./pettyCashApi";

// Standard fine-grained permission codes (server-branch-scoped already, same
// shape as Stock's useStockPermissions.js). Approve/Reject/Pay on
// vouchers/refills/reconciliations are NOT single declarative policies on
// the API - the real check combines these with Admin.SelfApproval and, for
// vouchers, PettyCash.ApproveHighValueVoucher (see PettyCashController.cs).
// These booleans only decide whether to SHOW a button; the server remains
// the authority, so a wrongly-enabled button just surfaces the server's 400.
export const useCanViewBalance = () => useHasPermission("PettyCash.ViewBalance");
export const useCanViewPettyCash = () => useHasPermission("PettyCash.View");
export const useCanViewAllPettyCash = () => useHasPermission("PettyCash.ViewAll");
export const useCanViewLedger = () => useHasPermission("PettyCash.ViewLedger");
export const useCanViewReports = () => useHasPermission("PettyCash.ViewReports");
export const useCanManageFund = () => useHasPermission("PettyCash.ManageFund");
export const useCanManageCategories = () => useHasPermission("PettyCash.ManageCategories");
export const useCanCreateVoucher = () => useHasPermission("PettyCash.CreateVoucher");
export const useCanApproveVoucher = () => useHasPermission("PettyCash.ApproveVoucher");
export const useCanApproveHighValueVoucher = () => useHasPermission("PettyCash.ApproveHighValueVoucher");
export const useCanPayVoucher = () => useHasPermission("PettyCash.PayVoucher");
export const useCanRequestRefill = () => useHasPermission("PettyCash.RequestRefill");
export const useCanApproveRefill = () => useHasPermission("PettyCash.ApproveRefill");
// Gates "Credit to Fund" (the /issue endpoint, re-enabled by Batch179) on refills that were
// approved but never credited. New refills are credited on approval and never need it.
export const useCanIssueRefill = () => useHasPermission("PettyCash.IssueRefill");
export const useCanReconcile = () => useHasPermission("PettyCash.Reconcile");
export const useCanApproveReconciliation = () => useHasPermission("PettyCash.ApproveReconciliation");
export const useCanAdjust = () => useHasPermission("PettyCash.Adjustment");
export const useCanSelfApprove = () => useHasPermission("Admin.SelfApproval");

// The self-pay ceiling comes from its own endpoint, NOT the generic Settings
// store it originally read from: GET /settings is gated
// Settings.ManageIntegrations, so for an ordinary requester it 403'd, the
// ceiling came back null, and VoucherDetail's selfPayEligible was false -
// hiding the Pay button on their own under-ceiling voucher even though the
// server would have allowed the payment. Returns null while loading or if
// unset (self-pay genuinely unavailable in that case).
export const useSelfPayCeiling = () => {
  const { data } = useGetSelfPayCeilingQuery();
  const value = data != null ? Number(data) : null;
  return Number.isFinite(value) ? value : null;
};

// Cancel a voucher that is already APPROVED, or one raised by someone else
// (Batch146). A requester withdrawing their OWN still-submitted voucher never
// needed this and still doesn't - the server allows that path on
// PettyCash.CreateVoucher alone. PAID is terminal either way: reversing a
// payment is a refund/adjustment, not a cancellation.
export const useCanCancelAnyVoucher = () => useHasPermission("PettyCash.CancelVoucher");

// Record the unspent balance handed back against a PAID voucher (Batch168).
// Seeded to every role/user that already holds PettyCash.PayVoucher - whoever
// paid the cash out takes the change back.
export const useCanRecordReturn = () => useHasPermission("PettyCash.RecordReturn");
